import { useParams, Routes, Route } from 'react-router-dom'
import { gql } from 'graphql-request'
import { GraduationCap, Route as RouteIcon, Users, Settings2, BotMessageSquare, FlaskConical } from 'lucide-react'

import { useQuery } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import Breadcrumbs from '@components/Breadcrumbs'
import Spinner from '@components/Spinner'
import NavTabs from '@components/NavTabs'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'

import TutorList from './TutorList'
import CoursesList from './CoursesList'
import StudentProfilesList from './StudentProfilesList'
import Collaborators from './Collaborators'
import Settings from './Settings'

const CLASSROOM_QUERY = gql`
  query classroom($id: ID!) {
    classroom(id: $id) {
      id
      name
      shortCode
      archived
      research
      educator {
        id
        email
        fullName
      }
      collaborators {
        id
        email
        fullName
      }
    }
  }
`

const ClassroomDetails = () => {
  const {
    user: { profile_id: currentUserProfileId },
    currentMembership: { organization: { features } }
  } = useCurrentUser()

  const collaboratorsEnabled = features.includes('collaborators')
  const { id } = useParams()

  const {
    isLoading,
    data: { classroom = {} } = {},
    refetch
  } = useQuery({
    queryKey: ['classroom', id],
    gqlQuery: CLASSROOM_QUERY,
    variables: { id }
  })

  const isOwner = classroom?.educator?.id === currentUserProfileId

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Link label='Classrooms' to='/educators/classrooms' />
        <Breadcrumbs.Text label={classroom.name} />

        <div className='flex items-center'>
          <If condition={classroom.archived}>
            <Badge variant='outline'>Archived</Badge>
          </If>
          <If condition={classroom.research}>
            <Tooltip>
              <TooltipTrigger asChild>
                <FlaskConical size={16} id='research-icon' className='ml-2 inline-flex' />
              </TooltipTrigger>
              <TooltipContent>This is a research classroom</TooltipContent>
            </Tooltip>
          </If>
        </div>
      </Breadcrumbs>

      <Choose>
        <When condition={isLoading}>
          <Spinner className='relative left-0 top-0 flex h-screen w-full items-center justify-center' />
        </When>

        <Otherwise>
          <h1 className='text-2xl font-bold text-foreground m-5'>{classroom.name}</h1>

          <NavTabs className='mx-5'>
            <NavTabs.Link
              to=''
              end
              icon={<BotMessageSquare size={16} />}
              label='Tutors'
            />
            <NavTabs.Link
              to='courses'
              end
              icon={<RouteIcon size={16} />}
              label='Pathways'
            />
            <NavTabs.Link
              to='students'
              label='Students'
              icon={<GraduationCap size={16} />}
            />
            <If condition={collaboratorsEnabled}>
              <NavTabs.Link
                to='collaborators'
                label='Collaborators'
                icon={<Users size={16} />}
              />
            </If>
            <If condition={isOwner}>
              <NavTabs.Link
                to='settings'
                label='Settings'
                icon={<Settings2 size={16} />}
              />
            </If>
          </NavTabs>

          <Routes>
            <Route
              index
              element={<TutorList id={id} />}
            />

            <Route
              path='courses'
              element={<CoursesList id={id} />}
            />

            <Route
              path='students'
              element={<StudentProfilesList id={id} name={classroom.name} shortCode={classroom.shortCode} isOwner={isOwner} />}
            />

            <Route
              path='collaborators'
              element={
                <Collaborators
                  classroomId={classroom.id}
                  collaborators={classroom.collaborators}
                  name={classroom.name}
                  classroomOwner={classroom.educator}
                  refetch={refetch}
                />
              }
            />

            <Route
              path='settings'
              element={
                <Settings
                  classroomId={classroom.id}
                  name={classroom.name}
                  archived={classroom.archived}
                  research={classroom.research}
                  refetch={refetch}
                />
              }
            />
          </Routes>
        </Otherwise>
      </Choose>
    </>
  )
}

export default ClassroomDetails
